import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2 } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { nameSchema } from "@/features/auth/domain/validation/commonSchemas";
import type { Profile } from "../../domain/entities/Profile";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Profile;
  /** Rejects with the API error so the dialog can stay open and show it. */
  onSave: (name: string) => Promise<void>;
}

const readOnlyFieldClass =
  "h-10 w-full cursor-not-allowed border-transparent bg-[#DDE1EA] text-gray-500 disabled:opacity-100";

const RequiredMark = () => <span className="text-destructive-text"> *</span>;

export const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
  open,
  onOpenChange,
  profile,
  onSave,
}) => {
  const { t } = useTranslation("profile");
  const { t: tAuth } = useTranslation("auth");
  const [name, setName] = React.useState(profile.name);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Start from the saved name each time the dialog opens.
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(profile.name);
      setNameError(null);
      setSaveError(null);
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (saving) return;
    onOpenChange(next);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const parsed = nameSchema.safeParse(trimmed);
    if (!parsed.success) {
      setNameError(tAuth(parsed.error.issues[0].message));
      return;
    }
    if (trimmed === profile.name) {
      onOpenChange(false);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(trimmed);
      onOpenChange(false);
    } catch (err) {
      const apiMessage = (err as { data?: { message?: string } })?.data?.message;
      setSaveError(apiMessage ?? t("editDialog.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-1rem)] gap-0 overflow-y-auto rounded-xl p-0 sm:max-w-[440px]">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle className="text-xl font-semibold">
            {t("editDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave}>
          <div className="space-y-4 px-5 py-5">
            <div className="space-y-2">
              <Label htmlFor="profile-name" className="text-sm font-normal text-gray-600">
                {t("editDialog.nameLabel")}
                <RequiredMark />
              </Label>
              <Input
                id="profile-name"
                placeholder={t("editDialog.namePlaceholder")}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(null);
                  setSaveError(null);
                }}
                aria-invalid={!!nameError}
                disabled={saving}
                autoFocus
                className={cn("h-10 w-full", nameError && "border-destructive")}
              />
              {nameError && <p className="text-xs text-destructive-text">{nameError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-email" className="text-sm font-normal text-gray-600">
                {t("editDialog.emailLabel")}
                <RequiredMark />
              </Label>
              <Input
                id="profile-email"
                type="email"
                value={profile.email}
                disabled
                className={readOnlyFieldClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-phone" className="text-sm font-normal text-gray-600">
                {t("editDialog.phoneLabel")}
                <RequiredMark />
              </Label>
              <div className="flex h-10 cursor-not-allowed items-center overflow-hidden rounded-lg bg-[#DDE1EA] px-2.5 text-gray-500 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-gray-500">
                <PhoneInput
                  id="profile-phone"
                  international
                  value={profile.phone}
                  onChange={() => {}}
                  disabled
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-role" className="text-sm font-normal text-gray-600">
                {t("editDialog.roleLabel")}
                <RequiredMark />
              </Label>
              <div className="relative">
                <Input
                  id="profile-role"
                  value={profile.role}
                  disabled
                  className={cn(readOnlyFieldClass, "pr-9")}
                />
                <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 text-gray-500" />
              </div>
            </div>

            {saveError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{saveError}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t px-5 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={saving}
              className="cursor-pointer border-primary px-5 text-primary hover:bg-[#EAF2FF] hover:text-primary"
            >
              {t("editDialog.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="cursor-pointer bg-primary px-5 text-white hover:bg-primary"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("editDialog.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
